import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import ExpensesTabSkeleton from '@/components/v2/molecules/admin/tabs/AdminExpensesTab/ExpensesTabSkeleton';
import ReceiptPreviewDialog from '@/components/v2/molecules/admin/tabs/shared/ReceiptPreviewDialog';
import BusinessExpenseFilters from '@/components/v2/molecules/admin/tabs/BusinessExpensesTab/BusinessExpenseFilters';
import TabEmptyState from '@/components/v2/atoms/admin/TabEmptyState';
import DeleteDialog from '@/components/v2/molecules/admin/tabs/shared/DeleteDialog';
import BusinessExpenseFormModal from '@/components/v2/organisms/admin/tabs/BusinessExpensesTab/BusinessExpenseFormModal';
import BusinessExpenseTable from '@/components/v2/organisms/admin/tabs/BusinessExpensesTab/BusinessExpenseTable';
import { BulkEditBusinessExpenseModal } from '@/components/v2/organisms/admin/tabs/BusinessExpensesTab/BulkEditBusinessExpenseModal';
import TablePagination from '@/components/v2/molecules/admin/shared/TablePagination';
import { useBusinessExpenseFilters } from '@/hooks/admin/tabs/useBusinessExpenseFilters';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import useTransactionModals from '@/hooks/admin/shared/useTransactionModals';
import {
  BusinessExpense,
  useDeleteBusinessExpense,
} from '@/hooks/useBusinessExpenses';
import { Plus, FileX } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useShiftSelect } from '@/hooks/useShiftSelect';

interface SelectionState {
  count: number;
  total: number;
  onDelete: () => void;
  onEdit?: () => void;
  isDeleting: boolean;
}

export interface BusinessTransactionsExpensesViewRef {
  downloadCSV: () => void;
  createNew: () => void;
  hasData: boolean;
}

interface BusinessTransactionsExpensesViewProps {
  onSelectionChange?: (selection: SelectionState | null) => void;
}

const BusinessTransactionsExpensesView = forwardRef<BusinessTransactionsExpensesViewRef, BusinessTransactionsExpensesViewProps>(
  ({ onSelectionChange }, ref) => {
    const { canCreate, canEdit, canDelete, canDownload, canClickPastDates } =
      useRolePermissions();

    const { toast } = useToast();
    const deleteExpenseMutation = useDeleteBusinessExpense();

    const {
      isReceiptModalOpen,
      receiptUrlToPreview,
      itemToEdit: expenseToEdit,
      isModalOpen: isExpenseModalOpen,
      isDeleteDialogOpen: isDeleteExpenseDialogOpen,
      setIsDeleteDialogOpen: setIsDeleteExpenseDialogOpen,
      handleNew: handleNewExpense,
      handleEdit: handleEditExpense,
      handleModalClose: handleExpenseModalClose,
      handleDelete: handleDeleteExpense,
      confirmDelete: confirmDeleteExpense,
      handleViewReceipt,
      handleReceiptModalClose,
    } = useTransactionModals<BusinessExpense>({
      onDelete: async (id) => deleteExpenseMutation.mutateAsync(id),
      withReceipt: true,
    });

    const {
      filteredData: filteredExpenses,
      isLoading: isLoadingExpenses,
      error: errorExpenses,
      refetch: refetchExpenses,
      searchTerm,
      setSearchTerm,
      selectedOrganizations,
      setSelectedOrganizations,
      selectedProperties,
      setSelectedProperties,
      selectedType,
      setSelectedType,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      typeOptions,
      hasActiveFilters,
      clearFilters,
      page,
      setPage,
      pageSize,
      totalCount,
      totalPages,
      fetchAllFilteredExpenses,
    } = useBusinessExpenseFilters();

    const {
      selectedItems: selectedExpenses,
      handleSelect: handleSelectExpense,
      handleSelectAll,
      clearSelection,
    } = useShiftSelect({ items: filteredExpenses });

    const [isDeleting, setIsDeleting] = useState(false);
    const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
    const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);

    const handleDownloadCSV = async () => {
      try {
        let expensesToExport: any[] = [];

        if (selectedExpenses.length > 0) {
          expensesToExport = filteredExpenses.filter(expense => selectedExpenses.includes(expense.id));
        } else {
          expensesToExport = await fetchAllFilteredExpenses();
        }

        const csvHeaders = [
          'Fecha',
          'Organización',
          'Propiedad',
          'Tipo de Egreso',
          'Subtipo',
          'Tipo de Pago',
          'Descripción',
          'Monto',
        ];

        const csvData = expensesToExport.map((expense: any) => [
          expense.payment_date || 'N/A',
          expense.organization_name || 'N/A',
          expense.property_short_name || 'N/A',
          expense.expense_type_name || 'N/A',
          expense.expense_subtype_name || 'N/A',
          expense.payment_type,
          expense.expense_description || 'N/A',
          expense.total_payment.toString(),
        ]);

        const csvContent = [
          csvHeaders.join(','),
          ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `egresos_negocio_${new Date().toISOString().split('T')[0]}.csv`,
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: 'CSV descargado',
          description: `Se han exportado ${expensesToExport.length} egreso(s)`,
        });
      } catch (error) {
        console.error('Error downloading CSV:', error);
        toast({
          title: 'Error',
          description: 'Error al descargar el CSV',
          variant: 'destructive',
        });
      }
    };

    const handleDeleteSelected = () => {
      setIsBulkDeleteDialogOpen(true);
    };

    const handleBulkEdit = () => {
      setIsBulkEditModalOpen(true);
    };

    const confirmBulkDelete = async () => {
      setIsDeleting(true);
      try {
        await Promise.all(
          selectedExpenses.map(expenseId =>
            deleteExpenseMutation.mutateAsync(expenseId)
          )
        );

        toast({
          title: "Éxito",
          description: `Se eliminaron ${selectedExpenses.length} egreso(s) correctamente`,
        });

        clearSelection();
        setIsBulkDeleteDialogOpen(false);
      } catch (error) {
        console.error("Error deleting selected expenses:", error);
        toast({
          title: "Error",
          description: "Error al eliminar los egresos seleccionados",
          variant: "destructive",
        });
      } finally {
        setIsDeleting(false);
      }
    };

    const selectedExpensesTotal = selectedExpenses.reduce((total, expenseId) => {
      const expense = filteredExpenses.find(e => e.id === expenseId);
      return total + (expense?.total_payment || 0);
    }, 0);

    // Exponer métodos al padre
    useImperativeHandle(ref, () => ({
      downloadCSV: handleDownloadCSV,
      createNew: handleNewExpense,
      hasData: filteredExpenses.length > 0,
    }));

    // Notify parent of selection changes
    useEffect(() => {
      if (selectedExpenses.length > 0) {
        onSelectionChange?.({
          count: selectedExpenses.length,
          total: selectedExpensesTotal,
          onDelete: handleDeleteSelected,
          onEdit: canEdit ? handleBulkEdit : undefined,
          isDeleting,
        });
      } else {
        onSelectionChange?.(null);
      }
    }, [selectedExpenses.length, selectedExpensesTotal, isDeleting, canEdit, onSelectionChange]);

    if (isLoadingExpenses) {
      return <ExpensesTabSkeleton />;
    }

    if (errorExpenses) {
      return (
        <TabErrorBanner
          title="Error al cargar egresos"
          message="No se pudieron cargar los egresos. Por favor, intenta nuevamente."
          onRetry={() => refetchExpenses()}
        />
      );
    }

    return (
      <div className="space-y-6">
        {/* Filtros */}
        <BusinessExpenseFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedOrganizations={selectedOrganizations}
          setSelectedOrganizations={setSelectedOrganizations}
          selectedProperties={selectedProperties}
          setSelectedProperties={setSelectedProperties}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          typeOptions={typeOptions}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
          canClickPastDates={canClickPastDates}
        />

        {filteredExpenses.length === 0 ? (
          <TabEmptyState
            icon={<FileX className="h-12 w-12" />}
            title="No hay egresos"
            description="No se encontraron egresos que coincidan con los filtros aplicados."
            actionLabel={canCreate ? "Crear Egreso" : undefined}
            onAction={canCreate ? handleNewExpense : undefined}
          />
        ) : (
          <>
            <BusinessExpenseTable
              expenses={filteredExpenses as any}
              canEdit={canEdit}
              canDelete={canDelete}
              canDownload={canDownload}
              onDelete={handleDeleteExpense}
              onEdit={handleEditExpense}
              onViewReceipt={handleViewReceipt}
              selectedExpenses={selectedExpenses}
              onSelectExpense={handleSelectExpense}
              onSelectAll={handleSelectAll}
            />

            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={() => { }}
            />
          </>
        )}

        <BusinessExpenseFormModal
          isOpen={isExpenseModalOpen}
          onClose={handleExpenseModalClose}
          editingExpense={expenseToEdit}
        />

        <BulkEditBusinessExpenseModal
          isOpen={isBulkEditModalOpen}
          onClose={() => {
            setIsBulkEditModalOpen(false);
            clearSelection();
          }}
          selectedExpenseIds={selectedExpenses}
        />

        <DeleteDialog
          title="¿Estás seguro?"
          description="Esta acción no se puede deshacer. Se eliminará permanentemente el registro de egreso."
          open={isDeleteExpenseDialogOpen}
          onOpenChange={setIsDeleteExpenseDialogOpen}
          onConfirm={confirmDeleteExpense}
        />

        <DeleteDialog
          title={`¿Eliminar ${selectedExpenses.length} egreso(s)?`}
          description={`Esta acción no se puede deshacer. Se eliminarán permanentemente ${selectedExpenses.length} registro(s) de egreso.`}
          open={isBulkDeleteDialogOpen}
          onOpenChange={setIsBulkDeleteDialogOpen}
          onConfirm={confirmBulkDelete}
          isLoading={isDeleting}
        />

        <ReceiptPreviewDialog
          title="Comprobante de pago"
          isOpen={isReceiptModalOpen}
          onClose={handleReceiptModalClose}
          receiptUrl={receiptUrlToPreview}
        />
      </div>
    );
  });

BusinessTransactionsExpensesView.displayName = 'BusinessTransactionsExpensesView';

export default BusinessTransactionsExpensesView;